import * as React from 'react';
import { AppendixFieldChange, ArticleAppendixVersion, ArticleList, ArticleType, fieldValidation, rng } from '../../../../../amorg-common';
import { articlesClientApi } from '../../../../../api/articlesClientApi';
import { TextAvatar } from '../../../../../designSystem/avatar/TextAvatar';
import { Button } from '../../../../../designSystem/button/Button';
import { ButtonRow } from '../../../../../designSystem/button/ButtonRow';
import { AnimatedCollapsibleContent } from '../../../../../designSystem/collapsible/AnimatedCollapsibleContent';
import { DeleteConfirmationButton } from '../../../../../designSystem/compositeComponents/DeleteConfirmationButton';
import { Flex } from '../../../../../designSystem/compositeComponents/FlexHelpers';
import { Divider } from '../../../../../designSystem/Divider';
import { DateInputField } from '../../../../../designSystem/formField/DateInputField';
import { SelectField } from '../../../../../designSystem/formField/SelectField';
import { TextField } from '../../../../../designSystem/formField/text/TextField';
import { TextSaveField } from '../../../../../designSystem/formField/text/TextSaveField';
import { TextAreaSaveField } from '../../../../../designSystem/formField/textArea/TextAreaSaveField';
import { LoadingPageOverlay } from '../../../../../designSystem/LoadingPageOverlay';
import { Modal } from '../../../../../designSystem/Modal';
import { Table } from '../../../../../designSystem/Table';
import { H3 } from '../../../../../designSystem/typography/Heading';


type ArticleViewAppendixPanelProps = {
    articleVersionId: string,
    types: ArticleType[],
    lists: ArticleList[],
    appendices: ArticleAppendixVersion[];
    canEdit:boolean;
    onChanged: () => Promise<void>;
};
export function ArticleViewAppendixPanel (props: ArticleViewAppendixPanelProps): JSX.Element
{
    const [show, setShow] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() =>
    {
        const newShow = props.appendices.length > 0;
        if (show !== newShow)
            setShow(newShow);
    }, [props.appendices]);

    async function handleDeleteAppendix (id: string)
    {
        setLoading(true);
        await articlesClientApi.deleteAppendix(id);
        await props.onChanged();
        setLoading(false);
    }

    return (
        <Flex flexDirection="column">
            <LoadingPageOverlay loading={loading} />
            <Flex>
                <Flex gap={8} style={{ flexGrow: 1 }}>
                    <H3>Ændringsbekendtgørelser</H3>
                    {props.appendices.length > 0 &&
                        <TextAvatar text={props.appendices.length.toString()} size='24px' />}
                </Flex>
                <Button variant='text' icon={show ? 'caret_caretUp' : 'caret_caretDown'} onClick={() => setShow(!show)} />
            </Flex>
            <AnimatedCollapsibleContent open={show}>
                <Flex flexDirection="column" gap={16}>
                    <Table>
                        <thead>
                            <tr>
                                <th>Titel</th>
                                <th>Type</th>
                                <th>Emne</th>
                                <th>Lovnummer</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {props.appendices.length === 0 &&
                                <tr>
                                    <td colSpan={4}>Der er ingen ændringsbekendtgørelser</td>
                                </tr>}
                            {props.appendices.sort((a, b) => {
                                return (b.date?.getTime() ?? -Infinity) - (a.date?.getTime() ?? -Infinity)
                            }).map((appendix) => <tr key={appendix.id}>
                                <td>{appendix.title}</td>
                                <td>{appendix.type.name}</td>
                                <td>{appendix.topic.name}</td>
                                <td>{appendix.reference}</td>
                                <td>
                                    <EditAppendixModalButton types={props.types} lists={props.lists} appendix={appendix}
                                        disabled={!props.canEdit} onChanged={props.onChanged} />
                                    <DeleteConfirmationButton
                                        asIcon
                                        requireKey
                                        variant='destructive-text'
                                        buttonText=''
                                        confirmationHeading='Slet ændringsbekendtgørelse'
                                        confirmationText={`Er du sikker på at du vil slette ændringsbekendtgørelsen ?`}
                                        disabled={!props.canEdit}
                                        onConfirmDelete={() => handleDeleteAppendix(appendix.id)} />
                                </td>
                            </tr>
                            )}
                        </tbody>
                    </Table>

                    <Divider />

                    <ButtonRow>
                        <CreateAppendixModalButton articleVersionId={props.articleVersionId} types={props.types} lists={props.lists}
                            disabled={!props.canEdit} onCreated={props.onChanged} />
                    </ButtonRow>

                </Flex>

            </AnimatedCollapsibleContent>
        </Flex>
    );
}

type CreateAppendixModalButtonProps = {
    articleVersionId: string,
    types: ArticleType[],
    lists: ArticleList[],
    disabled:boolean,
    onCreated: () => void
};
export function CreateAppendixModalButton (props: CreateAppendixModalButtonProps): JSX.Element
{
    const [show, setShow] = React.useState<boolean>(false);
    const [appendix, setAppendix] = React.useState<ArticleAppendixVersion>(getNewAppendix());

    function getNewAppendix (): ArticleAppendixVersion
    {
        return {
            id: rng.getRandomId(),
            appendixId: rng.getRandomId(),
            articleVersionId: props.articleVersionId,
            title: 'Ny ændringsbekendtgørelse',
            reference: '',
            url: '',
            date: null,
            type: { id: '', name: '' },
            topic: { id: '', name: '' },
            description: ''
        };
    }

    function handleClickShow ()
    {
        setAppendix(getNewAppendix());
        setShow(true);
    }

    async function handleClickCreate ()
    {
        await articlesClientApi.createAppendix(rng.getRandomId(), rng.getRandomId(), props.articleVersionId, appendix.type.id, appendix.topic.id, appendix.title);
        props.onCreated();
        setShow(false);
    }

    const valid = appendix.type.id !== '' && appendix.topic.id !== '' && fieldValidation.articleAppendixVersion.title(appendix.title);

    return (
        <>
            <Button variant='outlined' icon='actions_plus' text="Opret ny" disabled={props.disabled} onClick={handleClickShow} />
            <Modal title='Opret ændringsbekendtgørelse' showModal={show} onCancel={() => setShow(false)}
                content={
                    <Flex flexDirection='column' gap={16}>
                        <SelectField
                            label="Type lovkrav"
                            emptyText='Vælg type lovkrav'
                            selectedValue={appendix.type.id}
                            onChange={(value) => setAppendix({ ...appendix, type: { id: value, name: '' } })}
                            options={props.types.map(t => ({ text: t.name, value: t.id }))}
                        />

                        <SelectField
                            label='Liste/emne'
                            emptyText='Vælg emne'
                            selectedValue={appendix.topic.id}
                            onChange={(value) => setAppendix({ ...appendix, topic: { id: value, name: '' } })}
                            options={props.lists.map(l =>
                                ({ text: l.name, options: l.topics.map(t => ({ text: t.name, value: t.id })) }))}
                        />

                        <TextField
                            label='Titel'
                            value={appendix.title}
                            validator={(value: string) =>
                            {
                                return fieldValidation.articleAppendixVersion.title(value)
                                    ? { valid: true }
                                    : { valid: false, errorMessage: `Skal udfyldes. Maks. 200 (${value.length}) tegn` };
                            }}
                            onChange={(value) => setAppendix({ ...appendix, title: value })}
                        />

                        <ButtonRow>
                            <Button variant='outlined' text='Opret' disabled={!valid} onClick={handleClickCreate} />
                        </ButtonRow>
                    </Flex>
                } />
        </>
    );
}

type EditAppendixModalButtonProps = {
    types: ArticleType[],
    lists: ArticleList[],
    appendix: ArticleAppendixVersion,
    disabled: boolean,
    onChanged: () => Promise<void>;
};
export function EditAppendixModalButton (props: EditAppendixModalButtonProps): JSX.Element
{
    const [show, setShow] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);

    function handleClickShow ()
    {
        setShow(true);
    }

    async function handleUpdateField (change: AppendixFieldChange)
    {
        setLoading(true);
        await articlesClientApi.updateAppendixField(props.appendix.id, change);
        await props.onChanged();
        setLoading(false);
    }

    return (
        <>
            <Button variant='text' icon='interface_actions_edit' onClick={handleClickShow} />
            <Modal title='Rediger ændringsbekendtgørelse' showModal={show} onCancel={() => setShow(false)}
                content={
                    <Flex flexDirection='column' gap={16}>
                        <LoadingPageOverlay loading={loading} />

                        <TextSaveField
                            label='Titel'
                            value={props.appendix.title}
                            disabled={props.disabled} 
                            validator={(value: string) =>
                            {
                                return fieldValidation.articleAppendixVersion.title(value)
                                    ? { valid: true }
                                    : { valid: false, errorMessage: `Skal udfyldes. Maks. 200 (${value.length}) tegn` };
                            }}
                            onSave={(value, isValid) => isValid && handleUpdateField({ field: 'title', value })}
                        />

                        <TextSaveField
                            label='Lovnummer'
                            value={props.appendix.reference}
                            disabled={props.disabled} 
                            validator={(value: string) =>
                            {
                                return fieldValidation.articleAppendixVersion.reference(value)
                                    ? { valid: true }
                                    : { valid: false, errorMessage: `Skal udfyldes. Maks. 100 (${value.length}) tegn` };
                            }}
                            onSave={(value, isValid) => isValid && handleUpdateField({ field: 'reference', value })}
                        />

                        <Flex flexDirection='column' gap={4}>
                            <TextSaveField
                                label='Webaddresse'
                                value={props.appendix.url}
                                disabled={props.disabled}
                                validator={(value: string) =>
                                {
                                    return fieldValidation.articleAppendixVersion.url(value)
                                        ? { valid: true }
                                        : { valid: false, errorMessage: `URL skal udfyldes med en gyldig webaddresse der starter med https:// og er maks 500 tegn (${value.length})` };
                                }}
                                onSave={(value, isValid) => isValid && handleUpdateField({ field: 'url', value })}
                            />
                            <Flex justifyContent='flex-end'>
                                <a href={props.appendix.url.toLowerCase().startsWith('www')
                                        ? `http://${props.appendix.url}`
                                        : props.appendix.url
                                } target='_blank' rel='noreferrer'>Gå til webaddressen</a>
                            </Flex>
                        </Flex>

                        <SelectField
                            label="Type lovkrav"
                            selectedValue={props.appendix.type.id}
                            disabled={props.disabled} 
                            onChange={(value) => handleUpdateField({ field: 'typeId', value })}
                            options={props.types.map(t => ({ text: t.name, value: t.id }))}
                        />

                        <SelectField
                            label='Liste/emne'
                            selectedValue={props.appendix.topic.id}
                            disabled={props.disabled} 
                            onChange={(value) => handleUpdateField({ field: 'topicId', value })}
                            options={props.lists.map(l =>
                                ({ text: l.name, options: l.topics.map(t => ({ text: t.name, value: t.id })) }))}
                        />

                        <DateInputField
                            label='Dato'
                            value={props.appendix.date}
                            disabled={props.disabled} 
                            onValueChange={(value) => handleUpdateField({ field: 'date', value })}
                            validator={() => { return { valid: true }; }}
                            showClearButton
                        />

                        <TextAreaSaveField
                            label='Beskrivelse'
                            minRows={3}
                            value={props.appendix.description}
                            disabled={props.disabled} 
                            validator={(value: string) =>
                            {
                                return fieldValidation.articleAppendixVersion.description(value)
                                    ? { valid: true }
                                    : { valid: false, errorMessage: `Skal udfyldes. Maks. 5000 (${value.length}) tegn` };
                            }}
                            onSave={(value, isValid) => isValid && handleUpdateField({ field: 'description', value })}
                        />

                        <ButtonRow>
                            <Button variant='outlined' text='Luk' onClick={() => setShow(false)} />
                        </ButtonRow>

                    </Flex>
                } />
        </>
    );
}